<template>
  <div class="p-6 bg-white/60 rounded-xl min-h-screen">
    <!-- Header -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
      <h5 class="text-lg font-bold text-gray-800">
        <span class="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500">
          گزارش انتقال بین انبارها
        </span>
      </h5>
      <div class="flex items-center gap-2">
        <button @click="resetFilters"
                class="px-3 py-1.5 text-sm rounded-full bg-gray-700 text-white hover:bg-gray-800">
          حذف فیلترها
        </button>
        <button @click="applyFilters"
                class="px-3 py-1.5 text-sm rounded-full bg-blue-600 text-white hover:bg-blue-700">
          اعمال فیلتر
        </button>
      </div>
    </div>

    <div class="bg-white/70 backdrop-blur rounded-xl p-3 mb-6 shadow-sm border border-gray-100">
      <div class="grid grid-cols-1 md:grid-cols-12 gap-2">
        <div class="md:col-span-2">
          <input v-model.trim="filters.sku" placeholder="SKU"
                 @keyup.enter="applyFilters"
                 class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full text-right">
        </div>

        <div class="md:col-span-2">
          <input v-model.number="filters.product" type="number" min="1" placeholder="شناسه محصول"
                 @keyup.enter="applyFilters"
                 class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full text-right">
        </div>

        <div class="md:col-span-2">
          <store-select placeholder="انبار مبدأ"
                        v-model="filters.from_store" />
        </div>

        <div class="md:col-span-2">
          <store-select placeholder="انبار مقصد"
                        v-model="filters.to_store" />
        </div>

        <div class="md:col-span-2">
          <select v-model="filters.ordering"
                  class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full text-right">
            <option value="-performed_at">مرتب‌سازی: جدیدترین</option>
            <option value="performed_at">مرتب‌سازی: قدیمی‌ترین</option>
            <option value="-quantity">بیشترین تعداد</option>
            <option value="quantity">کمترین تعداد</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Error banner -->
    <div v-if="errorMsg"
         class="mb-4 rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm p-3">
      {{ errorMsg }}
    </div>

    <!-- Table -->
    <div class="bg-white/70 backdrop-blur rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50/80">
          <tr>
            <th class="p-3 text-[10px] text-gray-500">#</th>
            <th class="p-3 text-[10px] text-gray-500">زمان</th>
            <th class="p-3 text-[10px] text-gray-500">SKU</th>
            <th class="p-3 text-[10px] text-gray-500">محصول</th>
            <th class="p-3 text-[10px] text-gray-500">از انبار</th>
            <th class="p-3 text-[10px] text-gray-500">به انبار</th>
            <th class="p-3 text-[10px] text-gray-500 text-center">تعداد</th>
            <th class="p-3 text-[10px] text-gray-500">ثبت‌کننده</th>
            <th class="p-3 text-[10px] text-gray-500">توضیحات</th>
          </tr>
          </thead>

          <tbody>
          <tr v-if="items.length === 0">
            <td colspan="9" class="p-6 text-center text-gray-500">موردی یافت نشد</td>
          </tr>

          <tr v-else v-for="row in items" :key="row.id" class="border-t border-gray-100">
            <td class="p-3 text-[11px] text-gray-700">#{{ row.id }}</td>
            <td class="p-3 text-[11px] text-gray-700 whitespace-nowrap">
              {{ fmtDate(row.performed_at) }}
            </td>
            <td class="p-3 text-[11px] text-gray-700">{{ row.product_sku || '-' }}</td>
            <td class="p-3">
              <div class="text-[11px] font-medium text-gray-900">{{ row.product_name }}</div>
              <div class="text-[10px] text-gray-500">ID: {{ row.product_id }}</div>
            </td>
            <td class="p-3">
              <div class="text-[11px] text-gray-800">{{ row.from_store_name }}</div>
              <div class="text-[10px] text-gray-500">ID: {{ row.from_store_id }}</div>
            </td>
            <td class="p-3">
              <div class="text-[11px] text-gray-800">{{ row.to_store_name }}</div>
              <div class="text-[10px] text-gray-500">ID: {{ row.to_store_id }}</div>
            </td>
            <td class="p-3 text-center">
              <span class="inline-flex items-center justify-center rounded-md bg-gray-100 px-2 py-1 text-[11px] text-gray-700">
                {{ row.quantity }}
              </span>
            </td>
            <td class="p-3 text-[11px] text-gray-700">
              <div>{{ row.performed_by_name || '-' }}</div>
              <div class="text-[10px] text-gray-500" v-if="row.performed_by_id">ID: {{ row.performed_by_id }}</div>
            </td>
            <td class="p-3 text-[11px] text-gray-600 truncate max-w-xs" :title="row.note">{{ row.note }}</td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="p-4 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
        <div class="text-sm text-gray-600">
          نمایش {{ (currentPage - 1) * itemsPerPage + (items.length ? 1 : 0) }} تا
          {{ Math.min(currentPage * itemsPerPage, total) }} از {{ total }} رکورد
        </div>
        <div class="flex gap-1">
          <button @click="prevPage" :disabled="currentPage === 1"
                  class="p-2 border border-gray-200 rounded-lg bg-white/50 hover:bg-gray-50 disabled:opacity-50">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
          <button v-for="p in visiblePages" :key="p" @click="goToPage(p)"
                  class="p-2 text-center border border-gray-200 rounded-full"
                  :class="{
                    'bg-gradient-to-r from-blue-500/60 to-blue-600/50 text-white shadow-md': p === currentPage,
                    'bg-white/50 hover:bg-gray-50': p !== currentPage,
                    'cursor-default': p === '...'
                  }" :disabled="p === '...'">
            {{ p }}
          </button>
          <button @click="nextPage" :disabled="currentPage * itemsPerPage >= total"
                  class="p-2 border border-gray-200 rounded-lg bg-white/50 hover:bg-gray-50 disabled:opacity-50">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StoreSelect from "@/components/selects/StoreSelect";

export default {
  name: 'StoreToStoreTransferReport',
  components: { StoreSelect },
  data() {
    return {
      loading: true,
      errorMsg: '',
      items: [],
      total: 0,
      currentPage: 1,
      itemsPerPage: 10,
      filters: {
        sku: '',
        product: '',
        from_store: '',
        to_store: '',
        created_by: '',
        date_from: '',
        date_to: '',
        ordering: '-performed_at',
      }
    }
  },
  computed: {
    visiblePages() {
      const totalPages = Math.ceil(this.total / this.itemsPerPage) || 1
      const range = 1
      const pages = [1]
      if (this.currentPage - range > 2) pages.push('...')
      for (let i = Math.max(2, this.currentPage - range); i <= Math.min(totalPages - 1, this.currentPage + range); i++) {
        pages.push(i)
      }
      if (this.currentPage + range < totalPages - 1) pages.push('...')
      if (totalPages > 1) pages.push(totalPages)
      return pages
    },
  },
  methods: {
    fmtDate(dt) {
      // اگر کتابخونه تاریخ فارسی داری، اینجا استفاده کن
      try {
        const d = new Date(dt)
        return d.toLocaleString()
      } catch (_) { return dt }
    },
    applyFilters() {
      this.currentPage = 1
      this.fetchItems()
    },
    resetFilters() {
      this.filters = {
        sku: '',
        product: '',
        from_store: '',
        to_store: '',
        created_by: '',
        date_from: '',
        date_to: '',
        ordering: '-performed_at',
      }
      this.applyFilters()
    },
    buildParams() {
      const params = {
        limit: this.itemsPerPage,
        offset: (this.currentPage - 1) * this.itemsPerPage,
      }
      Object.entries(this.filters).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) params[k] = v
      })
      return params
    },
    fetchItems() {
      this.loading = true
      this.errorMsg = ''
      this.request({
        url: this.endpoint('store_handle/storeToStoreInventoryReport'),
        method: 'get',
        params: this.buildParams(),
        success: (data) => {
          this.items = data.results || []
          this.total = data.count || 0
          this.loading = false
        },
        error: (err) => {
          this.loading = false
          this.items = []
          this.total = 0
          this.errorMsg = err?.response?.data?.detail || 'خطا در دریافت گزارش'
        }
      })
    },
    prevPage() { if (this.currentPage > 1) { this.currentPage--; this.fetchItems() } },
    nextPage() { if (this.currentPage * this.itemsPerPage < this.total) { this.currentPage++; this.fetchItems() } },
    goToPage(p) { if (p !== '...') { this.currentPage = p; this.fetchItems() } },
  },
  mounted() {
    // اگر از route پارامتر اولیه داری، همینجا ست کن (اختیاری)
    this.fetchItems()
  }
}
</script>

<style>
body { direction: rtl; text-align: right; }
</style>
